import { c as _c } from "react/compiler-runtime";
import { FormLabel, FormControl, Stack, Divider, Box, Select, MenuItem, Tooltip } from '@mui/material';
import { AccountState, type Account } from '@repo/api-gw-sdk';
import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { FormTextField } from '@/components/form/formTextField';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import type { BackupVault } from '@/data/vaults/backupVault';
import { getDocsUrl } from '@/docs/docsUrls';
import { mrapAliasFieldValidation } from './byovPolicyUtils';
import { MrapCopyablePolicy } from './mrapCopyablePolicy';
import { VaultOwnBucketCopyableAllAccountsPolicy } from './vaultOwnBucketCopyablePolicy';
import { VaultOwnBucketEditKeyPolicyButton } from './vaultOwnBucketEditKeyPolicyButton';
import { BottomDialog } from '../dialogs/bottomDialog';
import { PanelWrapper } from '../panels/panelWrapper';
import { useBuckets } from '../restore/useBuckets';
import { SelectPlaceholder } from '../selectPlaceholder';
import { ReadMoreLink } from '../shared/readMoreLink';
export const SelectVaultBucketPanel = t0 => {
  const $ = _c(61);
  const {
    open,
    onClose,
    disabled,
    data,
    setData,
    vaultAccounts
  } = t0;
  const {
    byovEnabled: t1,
    EnableMrapForByov: t2
  } = useFeatureFlags();
  const byovEnabled = t1 === undefined ? false : t1;
  const enableMrapForByov = t2 === undefined ? false : t2;
  const enableMrap = byovEnabled && enableMrapForByov;
  let t3;
  if ($[0] !== data.vaultAttributes.aws?.mrapAlias) {
    t3 = data.vaultAttributes.aws?.mrapAlias?.trim() ?? "";
    $[0] = data.vaultAttributes.aws?.mrapAlias;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const mrapAlias = t3;
  let t4;
  if ($[2] !== data.vaultAccountId || $[3] !== vaultAccounts) {
    let t5;
    if ($[5] !== data.vaultAccountId) {
      t5 = account => account.id === data.vaultAccountId;
      $[5] = data.vaultAccountId;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    t4 = vaultAccounts.find(t5);
    $[2] = data.vaultAccountId;
    $[3] = vaultAccounts;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const selectedVaultAccount = t4;
  let t5;
  let t6;
  if ($[7] !== onClose) {
    t5 = {
      title: "Select bucket",
      subtitle: "Select a bucket from your self-managed vault account. You\u2019re responsible for the vault\u2019s security, availability, and ensuring Eon has access to manage snapshots.",
      fullWidthSubtitle: false,
      onClose
    };
    t6 = {
      primaryButton: {
        text: "Done",
        "data-testid": "done-button",
        onClick: onClose
      },
      secondaryButton: {
        text: "Cancel",
        "data-testid": "cancel-button",
        onClick: onClose
      }
    };
    $[7] = onClose;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Divider sx={{
      borderStyle: "dashed"
    }} />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = data.vaultAccountId || "";
  let t9;
  if ($[11] !== data || $[12] !== setData) {
    t9 = vaultAccountId => {
      setData({
        ...data,
        vaultAccountId,
        vaultAttributes: {
          ...data.vaultAttributes,
          aws: undefined
        }
      });
    };
    $[11] = data;
    $[12] = setData;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== disabled || $[15] !== t8 || $[16] !== t9 || $[17] !== vaultAccounts) {
    t10 = <VaultAccountSelector disabled={disabled} vaultAccountId={t8} setAccountId={t9} vaultAccounts={vaultAccounts} />;
    $[14] = disabled;
    $[15] = t8;
    $[16] = t9;
    $[17] = vaultAccounts;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== selectedVaultAccount) {
    t11 = selectedVaultAccount || {} as Account;
    $[19] = selectedVaultAccount;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = data.region || "";
  const t13 = data.vaultAttributes.aws?.bucketName || "";
  let t14;
  if ($[21] !== data || $[22] !== setData) {
    t14 = bucketName => {
      setData({
        ...data,
        vaultAttributes: {
          ...data.vaultAttributes,
          aws: {
            ...data.vaultAttributes.aws,
            bucketName
          }
        }
      });
    };
    $[21] = data;
    $[22] = setData;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== disabled || $[25] !== t11 || $[26] !== t12 || $[27] !== t13 || $[28] !== t14) {
    t15 = <BucketSelector account={t11} region={t12} disabled={disabled} bucketName={t13} setBucketName={t14} />;
    $[24] = disabled;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t10 || $[31] !== t15) {
    t16 = <NumericBulletSection bulletNumber={1} title="Select vault account and bucket"><BulletItem leftContent={<Stack gap="24px">{t10}{t15}</Stack>} /></NumericBulletSection>;
    $[30] = t10;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = data.vaultAttributes.aws?.bucketName || "";
  let t18;
  if ($[33] !== data.region || $[34] !== t17) {
    t18 = <VaultOwnBucketEditKeyPolicyButton region={data.region} bucketName={t17} />;
    $[33] = data.region;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = data.vaultAttributes.aws?.bucketName || "";
  let t20;
  if ($[36] !== t19) {
    t20 = <VaultOwnBucketCopyableAllAccountsPolicy bucketName={t19} />;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t18 || $[39] !== t20) {
    t21 = <NumericBulletSection bulletNumber={2} title="Add policy in AWS"><BulletItem title="Review and add this policy in your AWS account. Afterward, come back to this tab to finish the connection." leftContent={t18} rightContent={t20} /></NumericBulletSection>;
    $[38] = t18;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== data || $[42] !== enableMrap || $[43] !== setData) {
    t22 = enableMrap && <NumericBulletSection bulletNumber={3} title="Configure Multi-Region Access Point (optional)"><BulletItem title="If you need cross-region restore into isolated subnets, provide the alias of an S3 Multi-Region Access Point configured for this bucket." leftContent={<Stack gap="8px"><FormTextField size="small" title="" placeholder="e.g. mfzwi23gnjvgw.mrap" validation={mrapAliasFieldValidation} value={data.vaultAttributes.aws?.mrapAlias ?? ""} onChange={e => {
          setData({
            ...data,
            vaultAttributes: {
              ...data.vaultAttributes,
              aws: {
                ...data.vaultAttributes.aws,
                mrapAlias: e.target.value
              }
            }
          });
        }} dataTestId="vault-mrap-alias-input" /><ReadMoreLink href={getDocsUrl("awsRestoreVpcEndpoints")} /></Stack>} /></NumericBulletSection>;
    $[41] = data;
    $[42] = enableMrap;
    $[43] = setData;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== enableMrap || $[46] !== mrapAlias || $[47] !== selectedVaultAccount) {
    t23 = enableMrap && mrapAlias && <NumericBulletSection bulletNumber={4} title="Add MRAP policy in AWS"><BulletItem title="Review and add this policy on your Multi-Region Access Point. This allows Eon restore nodes to read objects through the MRAP." leftContent={<MrapCopyablePolicy mrapAlias={mrapAlias} vaultAccount={selectedVaultAccount} />} /></NumericBulletSection>;
    $[45] = enableMrap;
    $[46] = mrapAlias;
    $[47] = selectedVaultAccount;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== t16 || $[50] !== t21 || $[51] !== t22 || $[52] !== t23) {
    t24 = <Box padding="40px min(100px,6vw)" width="100%" height="auto"><Stack gap="40px" divider={t7}>{t16}{t21}{t22}{t23}</Stack></Box>;
    $[49] = t16;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t24 || $[55] !== t5 || $[56] !== t6) {
    t25 = <PanelWrapper header={t5} footer={t6}>{t24}</PanelWrapper>;
    $[54] = t24;
    $[55] = t5;
    $[56] = t6;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== open || $[59] !== t25) {
    t26 = <BottomDialog open={open} isFullScreen={true}>{t25}</BottomDialog>;
    $[58] = open;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  return t26;
};
const BucketSelector = t0 => {
  const $ = _c(11);
  const {
    disabled,
    bucketName,
    setBucketName,
    account,
    region
  } = t0;
  const {
    buckets,
    isLoading
  } = useBuckets(account, region);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FormLabel>Bucket</FormLabel>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = disabled || isLoading;
  const t3 = bucketName || "";
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "data-testid": "vault-bucket-name"
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== setBucketName) {
    t5 = event => {
      setBucketName(event.target.value);
    };
    $[2] = setBucketName;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== buckets) {
    t6 = buckets.map(_temp2);
    $[4] = buckets;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t5 || $[9] !== t6) {
    t7 = <FormControl size="small" className="w-full px-[0px]">{t1}<Select disabled={t2} size="small" value={t3} inputProps={t4} displayEmpty={true} renderValue={_temp} onChange={t5}>{t6}</Select></FormControl>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
const VaultAccountSelector = t0 => {
  const $ = _c(15);
  const {
    disabled,
    vaultAccountId,
    setAccountId,
    vaultAccounts
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FormLabel>Vault account</FormLabel>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = vaultAccountId || "";
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      "data-testid": "vault-bucket-name"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== vaultAccounts) {
    t4 = value => {
      if (!value) {
        return <SelectPlaceholder />;
      }
      return vaultAccounts.find(account => account.id === value)?.name || "";
    };
    $[2] = vaultAccounts;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== setAccountId || $[5] !== vaultAccountId) {
    t5 = event => {
      if (event.target.value === vaultAccountId || !event.target.value) {
        return;
      }
      setAccountId(event.target.value);
    };
    $[4] = setAccountId;
    $[5] = vaultAccountId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== vaultAccounts) {
    t6 = vaultAccounts.map(_temp3);
    $[7] = vaultAccounts;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== disabled || $[10] !== t2 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <FormControl size="small" className="w-full px-[0px]">{t1}<Select disabled={disabled} size="small" value={t2} inputProps={t3} displayEmpty={true} renderValue={t4} onChange={t5}>{t6}</Select></FormControl>;
    $[9] = disabled;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
function _temp(value) {
  if (!value) {
    return <SelectPlaceholder />;
  }
  return value;
}
function _temp2(bucket) {
  return <MenuItem key={bucket.bucketName} value={bucket.bucketName}>{bucket.bucketName}</MenuItem>;
}
function _temp3(account_0) {
  return <Tooltip key={account_0.id} title={account_0.state === AccountState.ACCOUNT_STATE_DISCONNECTED ? "Disconnected" : ""} placement="right"><span><MenuItem value={account_0.id} disabled={account_0.state === AccountState.ACCOUNT_STATE_DISCONNECTED}>{account_0.name}</MenuItem></span></Tooltip>;
}